<template>
	<v-card dark outlined class="fill-height" style="overflow:hidden; background-color:#1E1E1E;">

		<!-- DRAG HANDLE — required for vue-grid-layout -->
		<v-system-bar lights-out dark class="vue-draggable-handle" style="cursor:move">
			<v-icon small color="cyan">mdi-cloud-upload-outline</v-icon>
			<span class="white--text subtitle-2 text-uppercase ml-1 text-truncate">
				{{ $ml.get('cloud_queue_title') || 'Cloud Queue' }}
			</span>
			<v-spacer/>
			<v-btn icon x-small @click="fetchStats" :loading="loading">
				<v-icon x-small color="white">mdi-refresh</v-icon>
			</v-btn>
			<v-btn icon x-small class="error ml-1" @click="$emit('close_component', layout_id)">
				<v-icon x-small color="white">mdi-close</v-icon>
			</v-btn>
		</v-system-bar>

		<!-- CONTENT — must have no-drag class -->
		<div class="no-drag" style="height:calc(100% - 24px); overflow-y:auto; padding:10px;">

			<!-- Error -->
			<v-alert v-if="error" type="error" dense text class="caption mb-2">{{ error }}</v-alert>

			<!-- KPI row -->
			<v-row no-gutters class="mb-2">
				<v-col cols="4" class="pr-1">
					<v-card outlined class="pa-2 text-center" style="background-color:#2D2D2D; border-color:#424242;">
						<div class="caption grey--text" style="font-size:10px;">SENT</div>
						<div class="text-h6 font-weight-bold" style="color:#66BB6A;">{{ fmt(stats.sent) }}</div>
					</v-card>
				</v-col>
				<v-col cols="4" class="px-1">
					<v-card outlined class="pa-2 text-center" style="background-color:#2D2D2D; border-color:#424242;">
						<div class="caption grey--text" style="font-size:10px;">PENDING</div>
						<div class="text-h6 font-weight-bold d-flex align-center justify-center" style="color:#FFA726;">
							{{ fmt(stats.pending) }}
							<v-icon x-small :color="trendColor" class="ml-1">{{ trendIcon }}</v-icon>
						</div>
					</v-card>
				</v-col>
				<v-col cols="4" class="pl-1">
					<v-card outlined class="pa-2 text-center" style="background-color:#2D2D2D; border-color:#424242;">
						<div class="caption grey--text" style="font-size:10px;">TOTAL</div>
						<div class="text-h6 font-weight-bold white--text">{{ fmt(stats.total) }}</div>
					</v-card>
				</v-col>
			</v-row>

			<!-- Trend sparkline of pending -->
			<div class="mb-2">
				<div class="d-flex justify-space-between caption grey--text mb-1" style="font-size:10px;">
					<span>PENDING (trend)</span>
					<span :style="{ color: trendColor }">{{ trendLabel }}</span>
				</div>
				<div class="d-flex align-end" style="height:34px; gap:2px;">
					<div
						v-for="(v, i) in sparkBars"
						:key="i"
						:style="{ height: v + '%', backgroundColor: trendColor, flex: '1 1 0', borderRadius: '1px', opacity: 0.85 }"
					></div>
					<div v-if="!history.length" class="caption grey--text">no data yet…</div>
				</div>
			</div>

			<!-- Meta -->
			<div class="caption grey--text mb-2" style="font-size:11px;">
				<div><v-icon x-small color="grey">mdi-clock-outline</v-icon> Last sent: <span class="white--text">{{ lastSentLabel }}</span></div>
				<div v-if="topDevice"><v-icon x-small color="grey">mdi-camera-outline</v-icon> Top source: <span class="white--text">{{ shortId(topDevice.device_id) }}</span> ({{ fmt(topDevice.pending) }} pend.)</div>
			</div>

			<!-- Actions -->
			<div class="d-flex flex-wrap" style="gap:6px;">
				<v-btn x-small outlined color="green lighten-1" :loading="cleaning==='sent'" @click="clean('sent')">
					<v-icon x-small left>mdi-broom</v-icon>Clear sent
				</v-btn>
				<v-btn x-small outlined color="red lighten-1" :loading="cleaning==='all'" @click="confirmEmpty = true">
					<v-icon x-small left>mdi-delete-sweep</v-icon>Empty queue
				</v-btn>
			</div>
		</div>

		<!-- Confirm empty dialog -->
		<v-dialog v-model="confirmEmpty" max-width="340" dark>
			<v-card>
				<v-card-title class="subtitle-1">Empty the entire queue?</v-card-title>
				<v-card-text class="caption">
					This will delete all <b>{{ fmt(stats.total) }}</b> rows from the queue (sent and pending).
					This action cannot be undone.
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn text small @click="confirmEmpty = false">Cancel</v-btn>
					<v-btn color="error" small @click="clean('all'); confirmEmpty = false">Empty</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3500" top right>
			{{ snackbar.message }}
		</v-snackbar>
	</v-card>
</template>

<script>
export default {
	name : 'cloud_queue_widget',

	props : ['layout', 'layout_id', 'info_to_component', 'ref_to_component'],

	data() {
		return {
			loading  : false,
			cleaning : null,
			error    : null,
			stats    : { total: 0, sent: 0, pending: 0, by_device: [], last_sent_at: null },
			history  : [],          // últimos N valores de `pending`
			maxHistory : 24,
			confirmEmpty : false,
			pollId   : null,
			snackbar : { show: false, message: '', color: 'success' },
		};
	},

	computed : {
		topDevice() {
			return (this.stats.by_device && this.stats.by_device.length) ? this.stats.by_device[0] : null;
		},

		// Tendencia: compara el último pending con el anterior
		trend() {
			if (this.history.length < 2) return 0;
			return this.history[this.history.length - 1] - this.history[this.history.length - 2];
		},

		trendIcon() {
			if (this.trend > 0) return 'mdi-arrow-up-bold';
			if (this.trend < 0) return 'mdi-arrow-down-bold';
			return 'mdi-minus';
		},

		trendColor() {
			if (this.trend > 0) return '#EF5350'; // creciendo = malo
			if (this.trend < 0) return '#66BB6A'; // drenando = bueno
			return '#9E9E9E';
		},

		trendLabel() {
			if (this.trend > 0) return 'growing ▲';
			if (this.trend < 0) return 'draining ▼';
			return 'stable';
		},

		// Barras normalizadas (0-100%) del historial de pendientes
		sparkBars() {
			if (!this.history.length) return [];
			const max = Math.max(...this.history, 1);
			return this.history.map(v => Math.max(4, Math.round((v / max) * 100)));
		},

		lastSentLabel() {
			if (!this.stats.last_sent_at) return '—';
			try {
				const d = new Date(this.stats.last_sent_at);
				return d.toLocaleString('es-PE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
			} catch (e) {
				return String(this.stats.last_sent_at);
			}
		},
	},

	mounted() {
		this.fetchStats();
		this.pollId = setInterval(this.fetchStats, 10000);
	},

	beforeDestroy() {
		if (this.pollId) clearInterval(this.pollId);
	},

	methods : {
		fmt(v) {
			return (v == null) ? '0' : Number(v).toLocaleString();
		},

		shortId(id) {
			return id ? String(id).slice(0, 8) + '…' : '—';
		},

		async fetchStats() {
			this.loading = true;
			this.error = null;
			try {
				const res = await this.$axios.get(this.$URL + '/api/v1/eventqueue/stats');
				const d = res.data || {};
				this.stats = {
					total: d.total || 0,
					sent: d.sent || 0,
					pending: d.pending || 0,
					by_device: Array.isArray(d.by_device) ? d.by_device : [],
					last_sent_at: d.last_sent_at || null,
				};
				this.history.push(this.stats.pending);
				if (this.history.length > this.maxHistory) this.history.shift();
			} catch (err) {
				this.error = (err && err.message) ? err.message : 'Failed to load queue';
			} finally {
				this.loading = false;
			}
		},

		async clean(scope) {
			this.cleaning = scope;
			try {
				const res = await this.$axios.post(this.$URL + '/api/v1/eventqueue/clean', { scope });
				const deleted = (res.data && res.data.deleted != null) ? res.data.deleted : 0;
				this._snack(`Deleted ${Number(deleted).toLocaleString()} rows (${scope === 'all' ? 'whole queue' : 'sent'})`, 'success');
				this.history = [];
				await this.fetchStats();
			} catch (err) {
				this._snack((err && err.message) ? err.message : 'Failed to clean queue', 'error');
			} finally {
				this.cleaning = null;
			}
		},

		_snack(message, color) {
			this.snackbar.message = message;
			this.snackbar.color = color;
			this.snackbar.show = true;
		},
	},
};
</script>
